import type { LipDetection } from '../tracking/faceLandmarker';
import type { SemanticFaceMap } from './semanticFaceMap';
import { buildSemanticFaceMap, measureSemanticMaskCoverage } from './semanticFaceMap';
import {
  combineMasks,
  growMask,
  intersectMasks,
  maskAlphaCoverage,
  maskOverlapRatio,
  polishMask,
  subtractMasks,
  useMaskWhenCovered
} from '../rendering/maskUtils';
import { getOnnxBridge } from './onnxBridge';

const INPUT_SIZE = 512;

// Note: 'eye_g' / 'glasses' è una classe distinta nel modello CelebAMask-HQ
// (BiSeNet face-parsing) e copre l'intera area occhiali — un poligono che
// attraversa il viso. Inserirlo sia in leftEye sia in rightEye duplicava la
// regione e gonfiava ogni mask oculare ben oltre l'occhio reale, distorcendo
// downstream (growMask, sottrazioni dalle lid). La teniamo come gruppo a sé
// e la propaghiamo come "protezione" globale (cheek-skin, lid mobile) senza
// contaminare le mask occhio singole.
const LABEL_GROUPS = {
  skin: new Set(['skin', 'face', 'nose', 'neck']),
  hair: new Set(['hair']),
  ears: new Set(['l_ear', 'r_ear']),
  leftBrow: new Set(['l_brow', 'left_brow', 'left_eyebrow']),
  rightBrow: new Set(['r_brow', 'right_brow', 'right_eyebrow']),
  leftEye: new Set(['l_eye', 'left_eye']),
  rightEye: new Set(['r_eye', 'right_eye']),
  glasses: new Set(['eye_g', 'glasses']),
  upperLip: new Set(['u_lip', 'upper_lip']),
  lowerLip: new Set(['l_lip', 'lower_lip']),
  cloth: new Set(['cloth'])
};

const DEFAULT_LABELS = [
  'background',
  'skin',
  'l_brow',
  'r_brow',
  'l_eye',
  'r_eye',
  'eye_g',
  'l_ear',
  'r_ear',
  'ear_r',
  'nose',
  'mouth',
  'u_lip',
  'l_lip',
  'neck',
  'neck_l',
  'cloth',
  'hair',
  'hat'
];

interface ParsedFaceMasks {
  skin: HTMLCanvasElement;
  leftBrow: HTMLCanvasElement;
  rightBrow: HTMLCanvasElement;
  leftEye: HTMLCanvasElement;
  rightEye: HTMLCanvasElement;
  glasses: HTMLCanvasElement;
  lips: HTMLCanvasElement;
  hair: HTMLCanvasElement;
  ears: HTMLCanvasElement;
  cloth: HTMLCanvasElement;
}

function createMask(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

// Hair class index in DEFAULT_LABELS for CelebAMask-HQ / BiSeNet.
const HAIR_CLASS_IDX = 17;

export interface PrecomputedLabelMaps {
  // Argmax class per model pixel, row-major h×w. First maximum wins on ties.
  labels: Uint8Array;
  // P(hair) per model pixel (2-class softmax: hair vs strongest competitor),
  // recovering sub-pixel strand detail lost by argmax — a pixel at 70% hair
  // probability gets 179 alpha instead of binary 0/255. null when the output
  // is not float logits (class-id outputs get binary hair alpha instead).
  hairProb: Float32Array | null;
  w: number;
}

// Argmax and the hair softmax used to be recomputed for every OUTPUT pixel:
// on a 1280px photo that is ~1.6M pixels × 19 classes when the model grid has
// only 512² = 262k — nearest-neighbour upscale re-derived each model pixel ~6
// times, all on the main thread (visible jank on A13 iPhones in the
// 'adapting' stage). Precompute both once at model resolution; the upscale
// loop in buildParsedMasks then does plain lookups.
export function precomputeLabelMaps(
  data: Float32Array | Int32Array | BigInt64Array | number[],
  dims: readonly number[]
): PrecomputedLabelMaps {
  // Same useSoftHair condition as before: soft hair only for float logits.
  const soft = data instanceof Float32Array && (dims[0] ?? 1) === 1 && (dims[1] ?? 0) > 1;

  // Unexpected shapes labelled everything background before; keep that.
  if (dims.length < 2 || dims.length > 4) {
    const plane = INPUT_SIZE * INPUT_SIZE;
    return {
      labels: new Uint8Array(plane),
      hairProb: soft ? new Float32Array(plane) : null,
      w: INPUT_SIZE
    };
  }

  if (dims.length === 4) {
    const c = dims[1] ?? 0;
    const h = dims[2] ?? INPUT_SIZE;
    const w = dims[3] ?? INPUT_SIZE;
    const plane = h * w;
    const labels = new Uint8Array(plane);
    const hairProb = soft ? new Float32Array(plane) : null;
    const computeHair = HAIR_CLASS_IDX < c;
    for (let i = 0; i < plane; i++) {
      let best = 0;
      let bestScore = -Infinity;
      let hairLogit = -Infinity;
      let maxOtherLogit = -Infinity;
      for (let cls = 0; cls < c; cls++) {
        const score = Number(data[cls * plane + i] ?? 0);
        if (score > bestScore) {
          bestScore = score;
          best = cls;
        }
        if (cls === HAIR_CLASS_IDX) {
          hairLogit = score;
        } else if (score > maxOtherLogit) {
          maxOtherLogit = score;
        }
      }
      labels[i] = best;
      if (hairProb && computeHair) {
        // 2-class softmax, normalised for numerical stability.
        const maxLogit = Math.max(hairLogit, maxOtherLogit);
        const hairExp = Math.exp(hairLogit - maxLogit);
        const otherExp = Math.exp(maxOtherLogit - maxLogit);
        const denom = hairExp + otherExp;
        hairProb[i] = denom > 0 ? hairExp / denom : 0;
      }
    }
    return { labels, hairProb, w };
  }

  // 3D/2D outputs carry class ids directly (no logits). Preserve the previous
  // behaviour for the soft-hair edge: useSoftHair could be true for a float
  // 3D output, where hairProbabilityAt returned 0 for every pixel.
  const h = (dims.length === 3 ? dims[1] : dims[0]) ?? INPUT_SIZE;
  const w = (dims.length === 3 ? dims[2] : dims[1]) ?? INPUT_SIZE;
  const plane = h * w;
  const labels = new Uint8Array(plane);
  for (let i = 0; i < plane; i++) {
    // Clamp instead of letting the Uint8Array wrap: an out-of-range class id
    // must stay out of range (→ 'background'), not alias a real label.
    const v = Number(data[i] ?? 0);
    labels[i] = v > 0 ? (v < 255 ? v : 255) : 0;
  }
  return { labels, hairProb: soft ? new Float32Array(plane) : null, w };
}

function buildParsedMasks(
  outputData: Float32Array | Int32Array | BigInt64Array | number[],
  dims: readonly number[],
  width: number,
  height: number,
  labels = DEFAULT_LABELS
): ParsedFaceMasks {
  const masks = {
    skin: createMask(width, height),
    leftBrow: createMask(width, height),
    rightBrow: createMask(width, height),
    leftEye: createMask(width, height),
    rightEye: createMask(width, height),
    glasses: createMask(width, height),
    lips: createMask(width, height),
    hair: createMask(width, height),
    ears: createMask(width, height),
    cloth: createMask(width, height)
  };
  const contexts = Object.fromEntries(
    Object.entries(masks).map(([key, canvas]) => [key, canvas.getContext('2d')])
  ) as Record<keyof ParsedFaceMasks, CanvasRenderingContext2D | null>;
  const imageData = Object.fromEntries(
    Object.entries(masks).map(([key, canvas]) => [
      key,
      (canvas.getContext('2d') as CanvasRenderingContext2D).createImageData(width, height)
    ])
  ) as Record<keyof ParsedFaceMasks, ImageData>;

  // Argmax + hair probability precomputed once at model resolution — the
  // nearest-neighbour upscale below only does lookups (see precomputeLabelMaps).
  const { labels: labelMap, hairProb, w: mapW } = precomputeLabelMaps(outputData, dims);

  for (let y = 0; y < height; y++) {
    const sy = Math.min(INPUT_SIZE - 1, Math.floor((y / height) * INPUT_SIZE));
    for (let x = 0; x < width; x++) {
      const sx = Math.min(INPUT_SIZE - 1, Math.floor((x / width) * INPUT_SIZE));
      const si = sy * mapW + sx;
      const label = labels[labelMap[si] ?? 0] ?? 'background';
      const i = (y * width + x) * 4 + 3;
      if (LABEL_GROUPS.skin.has(label)) imageData.skin.data[i] = 255;
      if (LABEL_GROUPS.hair.has(label)) {
        // Soft hair probability: alpha = P(hair) × 255 for strand-level detail.
        // Falls back to binary 255 when logits are unavailable or the model
        // outputs class IDs instead of raw scores.
        const prob = hairProb ? (hairProb[si] ?? 0) : 1;
        imageData.hair.data[i] = Math.round(prob * 255);
      }
      if (LABEL_GROUPS.ears.has(label)) imageData.ears.data[i] = 255;
      if (LABEL_GROUPS.cloth.has(label)) imageData.cloth.data[i] = 255;
      if (LABEL_GROUPS.leftBrow.has(label)) imageData.leftBrow.data[i] = 255;
      if (LABEL_GROUPS.rightBrow.has(label)) imageData.rightBrow.data[i] = 255;
      if (LABEL_GROUPS.leftEye.has(label)) imageData.leftEye.data[i] = 255;
      if (LABEL_GROUPS.rightEye.has(label)) imageData.rightEye.data[i] = 255;
      if (LABEL_GROUPS.glasses.has(label)) imageData.glasses.data[i] = 255;
      if (LABEL_GROUPS.upperLip.has(label) || LABEL_GROUPS.lowerLip.has(label)) {
        imageData.lips.data[i] = 255;
      }
    }
  }

  for (const key of Object.keys(masks) as (keyof ParsedFaceMasks)[]) {
    contexts[key]?.putImageData(imageData[key], 0, 0);
  }

  return masks;
}

function alignPairByLandmarkOverlap(
  first: HTMLCanvasElement,
  second: HTMLCanvasElement,
  firstTarget: HTMLCanvasElement,
  secondTarget: HTMLCanvasElement
): { first: HTMLCanvasElement; second: HTMLCanvasElement; swapped: boolean } {
  const direct =
    maskOverlapRatio(first, firstTarget) +
    maskOverlapRatio(second, secondTarget);
  const swapped =
    maskOverlapRatio(second, firstTarget) +
    maskOverlapRatio(first, secondTarget);
  return swapped > direct
    ? { first: second, second: first, swapped: true }
    : { first, second, swapped: false };
}

function refineWithParsedMasks(base: SemanticFaceMap, parsed: ParsedFaceMasks): SemanticFaceMap {
  const minDim = Math.min(base.width, base.height);
  const growFine = Math.max(2, minDim * 0.004);
  const growProtect = Math.max(4, minDim * 0.009);
  const featherFine = Math.max(1.5, minDim * 0.0025);
  const featherSkin = Math.max(2, minDim * 0.004);

  const parsedSkin = polishMask(parsed.skin, { featherPx: featherSkin, threshold: 18 });
  const parsedLeftEye = polishMask(parsed.leftEye, { growPx: growFine, featherPx: featherFine });
  const parsedRightEye = polishMask(parsed.rightEye, { growPx: growFine, featherPx: featherFine });
  const parsedLeftBrow = polishMask(parsed.leftBrow, { growPx: growFine, featherPx: featherFine });
  const parsedRightBrow = polishMask(parsed.rightBrow, { growPx: growFine, featherPx: featherFine });
  const parsedLips = polishMask(parsed.lips, { growPx: growFine, featherPx: featherFine });
  const parsedHair = polishMask(parsed.hair, { growPx: growProtect * 2, featherPx: featherFine * 2 });
  // Ears: grown + feathered so the rim is covered. Subtracted from the hair
  // recolour so a cool shade doesn't cast grey on the (warm) ear that sits
  // surrounded by hair — the person-alpha can't reject it (ear is foreground).
  const parsedEars = polishMask(parsed.ears, { growPx: growProtect, featherPx: featherFine });
  // Cloth (collar, shirt): grown and feathered so it can be subtracted from
  // the hair recolour zone. This replaces the heuristic collarGuard luminance
  // key with the actual garment segmentation from the face-parser, preventing
  // dark shades (blue-black, espresso) from tinting a white shirt collar pink.
  const parsedCloth = polishMask(parsed.cloth, { growPx: growProtect * 2, featherPx: featherFine * 2 });
  // Mask occhiali: estesa più del normale (lenti + montatura) e usata come
  // protezione globale. Non confluisce in leftEye/rightEye singoli per non
  // gonfiare le statistiche oculari; protegge cheek-skin, lid mobili e
  // lash line dalla pittura sopra le lenti.
  const parsedGlasses = polishMask(parsed.glasses, { growPx: growProtect, featherPx: featherFine });
  const emptyMask = combineMasks(base.width, base.height, []);
  const eyePair = alignPairByLandmarkOverlap(
    parsedLeftEye,
    parsedRightEye,
    base.masks.leftEye,
    base.masks.rightEye
  );
  const browPair = alignPairByLandmarkOverlap(
    parsedLeftBrow,
    parsedRightBrow,
    base.masks.leftBrow,
    base.masks.rightBrow
  );
  const alignedLeftEye = eyePair.first;
  const alignedRightEye = eyePair.second;
  const alignedLeftBrow = browPair.first;
  const alignedRightBrow = browPair.second;
  const leftEyeRoi = growMask(base.masks.leftEye, growProtect);
  const rightEyeRoi = growMask(base.masks.rightEye, growProtect);
  const leftBrowRoi = growMask(base.masks.leftBrow, growProtect);
  const rightBrowRoi = growMask(base.masks.rightBrow, growProtect);
  const rescuedLeftEye = polishMask(intersectMasks(alignedLeftEye, [leftEyeRoi]), {
    growPx: growFine,
    featherPx: featherFine
  });
  const rescuedRightEye = polishMask(intersectMasks(alignedRightEye, [rightEyeRoi]), {
    growPx: growFine,
    featherPx: featherFine
  });
  const rescuedLeftBrow = polishMask(intersectMasks(alignedLeftBrow, [leftBrowRoi]), {
    growPx: growFine,
    featherPx: featherFine
  });
  const rescuedRightBrow = polishMask(intersectMasks(alignedRightBrow, [rightBrowRoi]), {
    growPx: growFine,
    featherPx: featherFine
  });

  const safeParsedLeftEye = useMaskWhenCovered(rescuedLeftEye, base.masks.leftEye, 0.0002, 0.08);
  const safeParsedRightEye = useMaskWhenCovered(rescuedRightEye, base.masks.rightEye, 0.0002, 0.08);
  const safeParsedLeftBrow = useMaskWhenCovered(rescuedLeftBrow, base.masks.leftBrow, 0.00015, 0.07);
  const safeParsedRightBrow = useMaskWhenCovered(rescuedRightBrow, base.masks.rightBrow, 0.00015, 0.07);
  const safeParsedLips = useMaskWhenCovered(
    intersectMasks(parsedLips, [growMask(base.masks.lips, growFine)]),
    base.masks.lips,
    0.0004,
    0.06
  );
  const safeParsedSkin = useMaskWhenCovered(parsedSkin, base.masks.skin, 0.04, 0.72);
  const safeParsedHair = useMaskWhenCovered(parsedHair, emptyMask, 0, 0.72);
  // Le lenti coprono al massimo ~12% del frame su un primo piano frontale —
  // qualsiasi cosa oltre il 18% è quasi certamente segmentation noise (es.
  // pelle scura confusa con eye_g). In quel caso falliamo aperto: nessuna
  // protezione occhiali, meglio un ombretto leggermente sopra al frame che
  // un buco esteso nel rendering.
  const safeParsedGlasses = useMaskWhenCovered(parsedGlasses, emptyMask, 0, 0.18);
  const fallbackChecks = [
    ['skin', parsedSkin, 0.04, 0.72],
    ['leftEye', rescuedLeftEye, 0.0002, 0.08],
    ['rightEye', rescuedRightEye, 0.0002, 0.08],
    ['leftBrow', rescuedLeftBrow, 0.00015, 0.07],
    ['rightBrow', rescuedRightBrow, 0.00015, 0.07],
    ['lips', intersectMasks(parsedLips, [growMask(base.masks.lips, growFine)]), 0.0004, 0.06],
    ['hair', parsedHair, 0, 0.72]
  ] as const;
  const fallbacks: string[] = fallbackChecks
    .filter(([, mask, min, max]) => {
      const coverage = maskAlphaCoverage(mask);
      return coverage < min || coverage > max;
    })
    .map(([key]) => key);

  const protectedFeatures = combineMasks(base.width, base.height, [
    growMask(safeParsedLeftEye, growProtect),
    growMask(safeParsedRightEye, growProtect),
    growMask(safeParsedLeftBrow, growProtect),
    growMask(safeParsedRightBrow, growProtect),
    growMask(safeParsedLips, growFine),
    safeParsedHair,
    safeParsedGlasses
  ]);
  const parsedFaceSkin = intersectMasks(safeParsedSkin, [base.masks.faceOval]);
  const cosmeticSkin = subtractMasks(parsedFaceSkin, [protectedFeatures]);
  const eyeLidProtectLeft = combineMasks(base.width, base.height, [
    growMask(safeParsedLeftEye, growFine),
    growMask(safeParsedLeftBrow, growProtect),
    safeParsedHair,
    safeParsedGlasses
  ]);
  const eyeLidProtectRight = combineMasks(base.width, base.height, [
    growMask(safeParsedRightEye, growFine),
    growMask(safeParsedRightBrow, growProtect),
    safeParsedHair,
    safeParsedGlasses
  ]);
  const safeLeftLid = polishMask(
    subtractMasks(base.masks.leftMobileLid, [eyeLidProtectLeft]),
    { featherPx: featherFine }
  );
  const safeRightLid = polishMask(
    subtractMasks(base.masks.rightMobileLid, [eyeLidProtectRight]),
    { featherPx: featherFine }
  );
  const safeLeftCreaseFade = polishMask(
    subtractMasks(base.masks.leftCreaseFade, [eyeLidProtectLeft]),
    { featherPx: Math.max(featherFine, minDim * 0.004) }
  );
  const safeRightCreaseFade = polishMask(
    subtractMasks(base.masks.rightCreaseFade, [eyeLidProtectRight]),
    { featherPx: Math.max(featherFine, minDim * 0.004) }
  );
  const safeLeftLashLine = subtractMasks(base.masks.leftLashLine, [
    growMask(safeParsedLeftBrow, growFine),
    safeParsedHair
  ]);
  const safeRightLashLine = subtractMasks(base.masks.rightLashLine, [
    growMask(safeParsedRightBrow, growFine),
    safeParsedHair
  ]);
  const safeLipBorder = useMaskWhenCovered(
    intersectMasks(base.masks.lipBorder, [growMask(safeParsedLips, growFine)]),
    base.masks.lipBorder,
    0.00008,
    0.04
  );
  const safeLeftCheek = intersectMasks(
    subtractMasks(base.masks.leftCheek, [protectedFeatures]),
    [cosmeticSkin]
  );
  const safeRightCheek = intersectMasks(
    subtractMasks(base.masks.rightCheek, [protectedFeatures]),
    [cosmeticSkin]
  );

  const masks = {
    ...base.masks,
    skin: cosmeticSkin,
    leftEye: safeParsedLeftEye,
    rightEye: safeParsedRightEye,
    leftBrow: safeParsedLeftBrow,
    rightBrow: safeParsedRightBrow,
    lips: safeParsedLips,
    leftMobileLid: safeLeftLid,
    rightMobileLid: safeRightLid,
    leftCreaseFade: safeLeftCreaseFade,
    rightCreaseFade: safeRightCreaseFade,
    leftLashLine: safeLeftLashLine,
    rightLashLine: safeRightLashLine,
    lipBorder: safeLipBorder,
    leftCheek: safeLeftCheek,
    rightCheek: safeRightCheek,
    // Expose the (noise-guarded, protection-grown) hair mask so the hair
    // recolour pass can use it. Slightly over-reaches the hairline by design
    // (it was built to protect makeup); fine for the spike.
    hair: safeParsedHair,
    ears: parsedEars,
    // Cloth mask: collar, shirt, jacket. Subtracted from hair recolour so
    // the tint stays off garments where person-alpha would otherwise fail
    // (both hair-and-collar are foreground, MODNet can't separate them).
    cloth: parsedCloth,
    // Full skin (hair NOT carved out, and NOT clipped to the face oval), for
    // the hair recolour to subtract. `skin` (cosmeticSkin) above has the hair
    // region removed AND is clipped to faceOval, so (a) it leaves a hair-shaped
    // hole at the hairline and (b) it excludes the jaw/chin/NECK — exactly where
    // long hair hangs and bleeds onto the showing neck skin. safeParsedSkin is
    // the raw ONNX skin group (skin/face/nose/neck), keeping the true skin/hair
    // boundary: real strands over the neck (labelled hair) still recolour, while
    // neck/jaw/forehead skin showing between them is protected.
    faceSkinFull: safeParsedSkin
  };

  return {
    ...base,
    source: 'face-parsing',
    masks,
    quality: {
      confidence: 0.82,
      parserAvailable: true,
      coverage: measureSemanticMaskCoverage(masks),
      fallbacks
    }
  };
}

export async function buildOnnxSemanticFaceMap(
  source: ImageBitmap | HTMLCanvasElement,
  detection: LipDetection,
  options: { requireReady?: boolean } = {}
): Promise<SemanticFaceMap | null> {
  try {
    const bridge = getOnnxBridge();
    if (options.requireReady && !bridge.isReady()) return null;

    // The ONNX worker receives the bitmap as a transferable. Never transfer
    // the user's master snapshot: downstream render passes still need it.
    const bitmap = await createImageBitmap(source);

    const result = await bridge.infer(bitmap);
    const parsed = buildParsedMasks(
      result.data as Float32Array | Int32Array | BigInt64Array | number[],
      result.dims,
      detection.imageWidth,
      detection.imageHeight
    );
    const base = await buildSemanticFaceMap(source, detection);
    return refineWithParsedMasks(base, parsed);
  } catch (err) {
    console.warn('[vto] ONNX face parsing unavailable, using landmark semantic fallback', err);
    return null;
  }
}

export function isOnnxSemanticParserReady(): boolean {
  return getOnnxBridge().isReady();
}

export function warmOnnxSemanticParser(): void {
  getOnnxBridge().warm();
}
